if (!customElements.get("low-power-video")) {
  customElements.define(
    "low-power-video",
    class LowPowerVideo extends HTMLElement {
      constructor() {
        super();
        this._onCanPlay = this._onCanPlay.bind(this);
        this._onVisibility = this._onVisibility.bind(this);
        this._onUserGesture = this._onUserGesture.bind(this);
        this._retryCount = 0;
        this._maxRetries = 6; // ~3s total with the delays below
      }

      connectedCallback() {
        this.video = this.querySelector("video");
        if (!this.video) return;

        // --- Ensure autoplay-friendly attributes (as attributes + properties)
        this.video.muted = true;
        this.video.autoplay = true;
        this.video.playsInline = true;

        this.video.setAttribute("muted", "");
        this.video.setAttribute("autoplay", "");
        this.video.setAttribute("playsinline", "");
        this.video.setAttribute("webkit-playsinline", ""); // iOS Safari quirk

        // Optional: loop if your design expects it
        if (!this.video.hasAttribute("loop")) this.video.setAttribute("loop", "");

        // --- Ensure we actually have a src; many themes lazy-load with data-*.
        // Move any data-src / data-srcset to real src/srcset before loading.
        const moveDataSrc = (el) => {
          const ds = el.getAttribute("data-src");
          const dss = el.getAttribute("data-srcset");
          if (ds && !el.getAttribute("src")) el.setAttribute("src", ds);
          if (dss && !el.getAttribute("srcset")) el.setAttribute("srcset", dss);
        };

        moveDataSrc(this.video);
        this.video.querySelectorAll("source").forEach(moveDataSrc);

        // --- Preload to reduce "play() promise rejected" from not-ready media
        if (!this.video.hasAttribute("preload")) this.video.setAttribute("preload", "auto");
        // If the theme forced "none", override it:
        if (this.video.getAttribute("preload") === "none") this.video.setAttribute("preload", "auto");

        // --- Listen for readiness + visibility to retry play
        this.video.addEventListener("canplay", this._onCanPlay, { passive: true });
        document.addEventListener("visibilitychange", this._onVisibility, { passive: true });

        // As a last resort, if the browser demands a user gesture, grab one.
        // (We immediately remove these after the first gesture.)
        window.addEventListener("touchstart", this._onUserGesture, { once: true, passive: true });
        window.addEventListener("click", this._onUserGesture, { once: true, passive: true });

        // Kick things off
        try {
          // Ensure the browser considers current sources
          this.video.load();
        } catch (_) {}
        this._attemptPlay();
      }

      disconnectedCallback() {
        if (!this.video) return;
        this.video.removeEventListener("canplay", this._onCanPlay);
        document.removeEventListener("visibilitychange", this._onVisibility);
        window.removeEventListener("touchstart", this._onUserGesture);
        window.removeEventListener("click", this._onUserGesture);
      }

      _onCanPlay() {
        this._attemptPlay(true);
      }

      _onVisibility() {
        if (document.visibilityState === "visible") this._attemptPlay();
      }

      _onUserGesture() {
        // If autoplay was blocked, a gesture should allow it now.
        this._attemptPlay(true);
      }

      _attemptPlay(force = false) {
        if (!this.video) return;

        // Some browsers pause background/hidden videos — try only when visible
        const isVisible = this._isElementInViewport(this.video);
        if (!isVisible && !force) {
          // Try again soon when the layout stabilizes/scroll occurs
          this._scheduleRetry();
          return;
        }

        // Make sure the video is actually muted & inline right before play
        this.video.muted = true;
        this.video.playsInline = true;

        const p = this.video.play();
        if (p && typeof p.then === "function") {
          p.then(() => {
            // success; nothing else to do
          }).catch(() => {
            // Autoplay blocked or not ready yet — keep trying briefly
            this._scheduleRetry();
          });
        }
      }

      _scheduleRetry() {
        if (this._retryCount >= this._maxRetries) return;
        const backoff = 200 + this._retryCount * 300; // 200ms, 500ms, 800ms, ...
        this._retryCount += 1;
        setTimeout(() => this._attemptPlay(), backoff);
      }

      _isElementInViewport(el) {
        try {
          const rect = el.getBoundingClientRect();
          return (
            rect.width > 0 &&
            rect.height > 0 &&
            rect.bottom > 0 &&
            rect.right > 0 &&
            rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
            rect.left < (window.innerWidth || document.documentElement.clientWidth)
          );
        } catch (_) {
          return true; // if something goes wrong, don't block autoplay attempt
        }
      }
    }
  );
}
